import 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {KDetailPage} from '@/pages/components/audit-program/k-section/constants';
import {K_1_PAGE} from '@/pages/components/audit-program/k-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface KDetailViewProps {
    header: AuditProgramSectionVO;
    page: KDetailPage;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.75rem', py: 0.75, px: 0.5};
/** Contain InputField default minWidth:110 so adjacent cells don't overlap */
const bodySx = {...cellBorder, py: 0.5, px: 0.5, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** K_1 — Single bank account confirmation / detail. */
export default function KDetailView({
    header,
    page,
    refCode = 'K_1',
    values,
    onChange,
}: KDetailViewProps) {
    const meta = {
        title: page.title || K_1_PAGE.title,
        subtitle: page.subtitle ?? K_1_PAGE.subtitle,
        accountLabel: page.accountLabel || K_1_PAGE.accountLabel,
        cyLabel: page.cyLabel || K_1_PAGE.cyLabel,
        lyLabel: page.lyLabel || K_1_PAGE.lyLabel,
        conclusionLabel: page.conclusionLabel || K_1_PAGE.conclusionLabel,
        conclusionPlaceholder: page.conclusionPlaceholder || K_1_PAGE.conclusionPlaceholder,
        remarksTitle: page.remarksTitle || K_1_PAGE.remarksTitle,
        remarks: page.remarks?.length ? page.remarks : K_1_PAGE.remarks,
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 0.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>
            {meta.subtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', mb: 1.5}}>
                    {meta.subtitle}
                </Typography>
            ) : (
                <Box sx={{mb: 1.5}}/>
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, maxWidth: 560}}>
                <Typography variant="body2" color="text.secondary" sx={{minWidth: 110, whiteSpace: 'nowrap'}}>
                    {meta.accountLabel}
                </Typography>
                <InputField
                    type="text"
                    fullWidth
                    compact
                    value={values.accountName ?? ''}
                    onChange={(e) => onChange('accountName', e.target.value)}
                />
            </Stack>

            <TableContainer sx={{mb: 2, maxWidth: 640}}>
                <Table size="small" sx={{tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 48}}/>
                        <col style={{width: 160}}/>
                        <col style={{width: 48}}/>
                        <col style={{width: 160}}/>
                        <col style={{width: 48}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{meta.cyLabel}</TableCell>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{meta.lyLabel}</TableCell>
                            <TableCell sx={headSx}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="center"
                                    sx={fieldSx}
                                    value={values['amount.tick'] ?? ''}
                                    onChange={(e) => onChange('amount.tick', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="right"
                                    sx={fieldSx}
                                    value={values['amount.cy'] ?? ''}
                                    onChange={(e) => onChange('amount.cy', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="center"
                                    sx={fieldSx}
                                    value={values['amount.cyMark'] ?? ''}
                                    onChange={(e) => onChange('amount.cyMark', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="right"
                                    sx={fieldSx}
                                    value={values['amount.ly'] ?? ''}
                                    onChange={(e) => onChange('amount.ly', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="center"
                                    sx={fieldSx}
                                    value={values['amount.lyTick'] ?? ''}
                                    onChange={(e) => onChange('amount.lyTick', e.target.value)}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack spacing={0.75} sx={{mb: 2, maxWidth: 720}}>
                <Typography variant="subtitle2" fontWeight={700}>{meta.conclusionLabel}</Typography>
                <InputField
                    type="text"
                    fullWidth
                    compact
                    multiline
                    minRows={2}
                    placeholder={meta.conclusionPlaceholder}
                    value={values.conclusion ?? ''}
                    onChange={(e) => onChange('conclusion', e.target.value)}
                />
            </Stack>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>
                {meta.remarksTitle}
            </Typography>
            <Stack spacing={0.25}>
                {meta.remarks.map((r, idx) => (
                    <Typography key={`${r.tick}-${idx}`} variant="body2" sx={{pl: 0.5, lineHeight: 1.4}}>
                        <Box component="span" sx={{display: 'inline-block', minWidth: 44, fontWeight: 600}}>
                            {r.tick}
                        </Box>
                        {r.text}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}
